import type { Money, VerifiedSlip } from '@pf/contracts';
import { ArrowRight, Check, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toEpochMs } from '@/lib/clock';
import { formatDateTime } from '@/lib/datetime';
import { decimalStringsEqual, formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';

/**
 * Reservation time and amount versus the slip's — เวลา + ยอด.
 *
 * Two checks a payee/payer name-and-bank diff cannot express:
 *
 *  - **Freshness.** A slip whose bank-recorded `transferred_at` predates the reservation cannot
 *    be evidence of *this* payment — the money moved before PaymentFlow ever offered this
 *    withdrawer to pay. SAD §6 already computes this as the `freshness` check; this row is what
 *    lets an operator see *why* without doing the arithmetic themselves.
 *  - **Amount.** Compared as decimal strings via `decimalStringsEqual`, never as parsed floats
 *    (docs/plan/06-risks.md R3) — the same rule every other money comparison in this console
 *    follows.
 */

/** Props for {@link TimeAmountDiff}. */
export interface TimeAmountDiffProps {
  /** When the reservation was made, or `null`/`undefined` when the case predates this field. */
  reservedAt: string | null | undefined;
  /** The reserved amount. */
  expectedAmount: Money;
  /** The verified slip, or `null` when none was ever verified. */
  slip: VerifiedSlip | null;
  /** Additional classes. */
  className?: string | undefined;
}

interface Row {
  label: string;
  expected: string;
  actual: string | null;
  matches: boolean | null;
}

/** Render the reservation-time-and-amount versus slip-time-and-amount comparison. */
export function TimeAmountDiff({
  reservedAt,
  expectedAmount,
  slip,
  className,
}: TimeAmountDiffProps): ReactNode {
  const { t } = useTranslation();

  const reservedAtMs =
    reservedAt === null || reservedAt === undefined ? null : toEpochMs(reservedAt);
  const transferredAtMs = slip === null ? null : toEpochMs(slip.transferred_at);

  const rows: Row[] = [
    {
      label: t('domain.timeAmountDiff.time'),
      expected:
        reservedAtMs === null || reservedAt == null
          ? t('domain.timeAmountDiff.reservedAtUnknown')
          : t('domain.timeAmountDiff.reservedAtValue', { when: formatDateTime(reservedAt) }),
      actual: slip === null ? null : formatDateTime(slip.transferred_at),
      matches:
        reservedAtMs === null || transferredAtMs === null ? null : transferredAtMs >= reservedAtMs,
    },
    {
      label: t('domain.timeAmountDiff.amount'),
      expected: formatMoney(expectedAmount, { withCode: true }),
      actual: slip === null ? null : formatMoney(slip.amount, { withCode: true }),
      matches:
        slip === null
          ? null
          : decimalStringsEqual(expectedAmount.value, slip.amount.value) &&
            expectedAmount.currency === slip.amount.currency,
    },
  ];

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', className)}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 border-b border-border bg-muted/50 px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{t('domain.timeAmountDiff.expectedHeader')}</span>
        <span className="sr-only">{t('domain.payeeDiff.comparedWith')}</span>
        <span aria-hidden="true" />
        <span>{t('domain.timeAmountDiff.attestedHeader')}</span>
      </div>

      <ul className="divide-y divide-border">
        {rows.map((row) => (
          <li
            key={row.label}
            className="grid grid-cols-1 gap-x-3 gap-y-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center"
            data-field={row.label}
            data-matches={row.matches === null ? 'unknown' : String(row.matches)}
          >
            <div className="min-w-0">
              <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                {row.label}
              </p>
              <p className="break-words text-sm text-foreground">{row.expected}</p>
            </div>

            <div className="hidden items-center justify-center sm:flex">
              {row.matches === true ? (
                <span
                  className="flex size-6 items-center justify-center rounded-full bg-state-good-bg text-state-good"
                  title={t('domain.payeeDiff.matchesTitle')}
                >
                  <Check className="size-3.5" aria-hidden="true" />
                  <span className="sr-only">{t('domain.payeeDiff.matchesSr')}</span>
                </span>
              ) : row.matches === false ? (
                <span
                  className="flex size-6 items-center justify-center rounded-full bg-state-bad-bg text-state-bad"
                  title={
                    row.label === t('domain.timeAmountDiff.time')
                      ? t('domain.timeAmountDiff.staleTitle')
                      : t('domain.payeeDiff.doesNotMatchTitle')
                  }
                >
                  <X className="size-3.5" aria-hidden="true" />
                  <span className="sr-only">{t('domain.payeeDiff.doesNotMatchSr')}</span>
                </span>
              ) : (
                <ArrowRight className="size-4 text-muted-foreground" aria-hidden="true" />
              )}
            </div>

            <div className="min-w-0">
              <p
                className={cn(
                  'break-words text-sm',
                  row.matches === false ? 'font-semibold text-state-bad' : 'text-foreground',
                )}
              >
                {row.actual ?? t('domain.timeAmountDiff.noSlipValue')}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
